import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, Box, Button, Alert,
  LinearProgress, CircularProgress, Stack, Chip, Skeleton
} from '@mui/material';
import Grid from '@mui/material/Grid';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import api from '../api';
import { API_CONFIG } from '../config';
import { useLanguage } from '../i18n/LanguageContext';
import InsightsCard, { InsightsResult } from './InsightsCard';

interface DashboardSummary {
  name: string;
  today_calories: number;
  calorie_budget: number;
  calories_by_type: Record<string, number>;
  current_weight: number;
  target_weight: number;
  start_weight: number;
  weight_remaining: number;
  progress_pct: number;
  avg_daily_calories: number;
  meals_logged_this_week: number;
  weight_change_week: number;
  current_streak: number;
  best_streak: number;
  logged_today: boolean;
  is_fasting_day?: boolean;
  fasting_budget?: number | null;
}

// ---------------------------------------------------------------------------
// sessionStorage-based stale-while-revalidate cache.
// Survives in-app navigation; automatically cleared on tab close.
// No cross-user leak risk (logout clears sessionStorage).
// ---------------------------------------------------------------------------
const DASH_CACHE_KEY = 'dashCache';
const INSIGHTS_CACHE_KEY = 'insightsCache';
const INSIGHTS_FAILED_KEY = 'insightsFailed';

function getDashCache(): DashboardSummary | null {
  try {
    const v = sessionStorage.getItem(DASH_CACHE_KEY);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
}

function setDashCache(data: DashboardSummary) {
  sessionStorage.setItem(DASH_CACHE_KEY, JSON.stringify(data));
}

function getInsightsCache(): InsightsResult | null {
  try {
    const v = sessionStorage.getItem(INSIGHTS_CACHE_KEY);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
}

function setInsightsCache(data: InsightsResult) {
  sessionStorage.setItem(INSIGHTS_CACHE_KEY, JSON.stringify(data));
}

function getInsightsFailed(): boolean {
  return sessionStorage.getItem(INSIGHTS_FAILED_KEY) === 'true';
}

function setInsightsFailed(v: boolean) {
  if (v) sessionStorage.setItem(INSIGHTS_FAILED_KEY, 'true');
  else sessionStorage.removeItem(INSIGHTS_FAILED_KEY);
}

export function clearDashboardCache() {
  sessionStorage.removeItem(DASH_CACHE_KEY);
  sessionStorage.removeItem(INSIGHTS_CACHE_KEY);
  sessionStorage.removeItem(INSIGHTS_FAILED_KEY);
}

const Dashboard: React.FC = () => {
  const cachedDash = getDashCache();
  const cachedInsights = getInsightsCache();
  const insightsFailed = getInsightsFailed();

  const [data, setData] = useState<DashboardSummary | null>(cachedDash);
  const [loading, setLoading] = useState(!cachedDash);
  const [summaryFailed, setSummaryFailed] = useState(false);

  const [insights, setInsights] = useState<InsightsResult | null>(cachedInsights);
  const [insightsLoading, setInsightsLoading] = useState(
    !cachedInsights && !insightsFailed
  );

  const navigate = useNavigate();
  const { t, locale } = useLanguage();

  // ---------------------------------------------------------------------------
  // fetchInsights: foreground fetch (shows skeleton).
  // Used on first load (no cache) and when the user hits the refresh button.
  // ---------------------------------------------------------------------------
  const fetchInsights = useCallback((force = false) => {
    setInsightsLoading(true);
    const endpoint = force
      ? API_CONFIG.endpoints.insights.dailyForce
      : API_CONFIG.endpoints.insights.daily;
    api.get(endpoint)
      .then(r => {
        setInsightsCache(r.data);
        setInsightsFailed(false);
        setInsights(r.data);
      })
      .catch((e) => {
        console.error('Dashboard: insights fetch failed:', e);
        setInsightsFailed(true);
        if (!getInsightsCache()) setInsights(null);
      })
      .finally(() => setInsightsLoading(false));
  }, []);

  const fetchSummary = useCallback(() => {
    setSummaryFailed(false);
    setLoading(prev => !getDashCache() ? true : prev);
    api.get('/dashboard/summary')
      .then(r => { setDashCache(r.data); setData(r.data); setSummaryFailed(false); })
      .catch((e) => {
        console.error('Dashboard: summary fetch failed:', e);
        if (!getDashCache()) setSummaryFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // --- Dashboard summary ---------------------------------------------------
    // Always re-fetch on mount. If cache exists, loading stays false and the
    // fresh data replaces stale data silently.
    fetchSummary();

    // --- Daily insights ------------------------------------------------------
    if (cachedInsights || insightsFailed) {
      // Have cache OR previously failed: silent background refresh (no skeleton)
      api.get(API_CONFIG.endpoints.insights.daily)
        .then(r => { setInsightsCache(r.data); setInsightsFailed(false); setInsights(r.data); })
        .catch((e) => {
          console.error('Dashboard: insights background refresh failed:', e);
          if (!getInsightsCache()) setInsightsFailed(true);
        });
    } else {
      // True first visit: fetch with loading state.
      fetchInsights(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const caloriePct = data && data.calorie_budget > 0
    ? Math.min(100, (data.today_calories / data.calorie_budget) * 100)
    : 0;

  const today = new Date().toLocaleDateString(locale, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <Container maxWidth="sm" sx={{ pt: 2 }}>
      {/* Error banner */}
      {summaryFailed && !data && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={fetchSummary}>{t.retry}</Button>}
        >
          {t.dashboardLoadFailed}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {loading ? (
          <>
            <Skeleton variant="text" width="55%" height={40} />
            <Skeleton variant="text" width="40%" />
          </>
        ) : data ? (
          <>
            <Typography variant="h5" fontWeight="bold">
              {t.hej}, {data.name}!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {today}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {(data.current_streak ?? 0) >= 2 && (
                <Chip
                  label={`🔥 ${t.dayStreak.replace('{n}', String(data.current_streak))}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {(data.best_streak ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t.bestStreak.replace('{n}', String(data.best_streak))}
                </Typography>
              )}
              {!data.logged_today && (
                <Typography variant="caption" color="text.secondary">
                  {t.logTodayStreak}
                </Typography>
              )}
            </Box>
          </>
        ) : null}
      </Box>

      <Stack spacing={2}>
        {/* Weight progress */}
        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            {loading ? (
              <>
                <Skeleton variant="text" width="45%" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Skeleton variant="circular" width={80} height={80} />
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
                    {[1, 2, 3].map(i => (
                      <Box key={i} sx={{ textAlign: 'center' }}>
                        <Skeleton variant="text" width={36} />
                        <Skeleton variant="text" width={48} height={28} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            ) : data ? (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t.weightProgress}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={data.progress_pct}
                      size={80}
                      thickness={4}
                      color="primary"
                    />
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography variant="body2" fontWeight="bold">
                        {Math.round(data.progress_pct)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{t.start}</Typography>
                        <Typography variant="body2" fontWeight="bold">{data.start_weight} kg</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{t.now}</Typography>
                        <Typography variant="body2" fontWeight="bold">{data.current_weight} kg</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{t.target}</Typography>
                        <Typography variant="body2" fontWeight="bold">{data.target_weight} kg</Typography>
                      </Box>
                    </Box>
                    {data.weight_remaining > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {data.weight_remaining} kg {t.remaining}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Today's calories */}
        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            {loading ? (
              <>
                <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                  <Skeleton variant="text" width={90} height={56} />
                  <Skeleton variant="text" width={70} />
                </Box>
                <Skeleton variant="rounded" height={8} sx={{ borderRadius: 4 }} />
              </>
            ) : data ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t.todaysCalories}
                  </Typography>
                  {data.is_fasting_day && (
                    <Chip label={t.fastingDay} size="small" color="warning" variant="outlined" sx={{ mb: 0.5 }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h4" fontWeight="bold">
                    {data.today_calories}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    / {data.calorie_budget} {t.kcal}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={caloriePct}
                  sx={{ mt: 1, mb: 1, height: 8, borderRadius: 4 }}
                  color={caloriePct > 100 ? 'error' : 'primary'}
                />
                {Object.keys(data.calories_by_type).length > 0 && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {Object.entries(data.calories_by_type).map(([type, kcal]) => (
                      <Typography key={type} variant="caption" color="text.secondary">
                        {type}: {Math.round(kcal)} {t.kcal}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Action buttons — always visible */}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6 }}>
            <Button
              variant="contained" fullWidth
              startIcon={<RestaurantIcon />}
              onClick={() => navigate('/app/meals')}
              sx={{ py: 1.5, borderRadius: 3 }}
            >
              {t.logMeal}
            </Button>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Button
              variant="outlined" fullWidth
              startIcon={<MonitorWeightIcon />}
              onClick={() => navigate('/app/weight')}
              sx={{ py: 1.5, borderRadius: 3 }}
            >
              {t.logWeight}
            </Button>
          </Grid>
        </Grid>

        {/* This week stats */}
        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            {loading ? (
              <>
                <Skeleton variant="text" width="30%" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[1, 2, 3].map(i => (
                    <Box key={i} sx={{ textAlign: 'center' }}>
                      <Skeleton variant="text" width={52} height={36} sx={{ mx: 'auto' }} />
                      <Skeleton variant="text" width={64} sx={{ mx: 'auto' }} />
                    </Box>
                  ))}
                </Box>
              </>
            ) : data ? (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t.thisWeek}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold">{data.avg_daily_calories}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.avgKcalDay}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold">{data.meals_logged_this_week}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.mealsLogged}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h6" fontWeight="bold"
                        color={data.weight_change_week <= 0 ? 'success.main' : 'error.main'}
                      >
                        {data.weight_change_week > 0 ? '+' : ''}{data.weight_change_week} kg
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t.weightChange}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Insights */}
        <InsightsCard
          loading={insightsLoading}
          daily={insights}
          onRefresh={() => fetchInsights(true)}
        />
      </Stack>
    </Container>
  );
};

export default Dashboard;
